import { memo, useState } from 'react'
import { useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'

// evertel
import { APIDataRoomMessage, APIDataThreadMessage } from '@evertel/types'
import { useService } from '@evertel/di'
import { Button, Col, Row, useUI } from '@evertel/web/ui'
import { botMessageActionsNavigationOptions } from '@evertel/web/utils'
import { MessageCTAController } from '../../../controllers/MessageCTAController'
import { InviteTabs, useInvite } from '@evertel/web/invites'
import { useCreateRoom, useJoinRoom } from '@evertel/web/room'
import { useCreateThread } from '@evertel/web/thread'

const MessageBodyAction: React.FC<{message: APIDataRoomMessage | APIDataThreadMessage}> = observer(({ message }) => {

    const messageActionController = useService(MessageCTAController, [])
    const navigate = useNavigate()
    const { addToast, leftNavOpen, toggleLeftNav } = useUI()
    const [isBusy, setIsBusy] = useState(false)

    // Modal hooks
    const { openInvite } = useInvite()
    const { openCreateRoom } = useCreateRoom()
    const { openCreateThread } = useCreateThread()
    const { openJoinRoom } = useJoinRoom()

    if (!message || !message.meta || !message.meta.cta || !message.meta.cta.length) return null

    const cta = message.meta?.cta // array

    // Filter out navigation CTAs that are mobile-only or don't have handlers
    const validCtas = cta.filter(item => {
        if (item.action === 'navigate') {
            const option = (item.params) ? botMessageActionsNavigationOptions.find(b => b.value === item.params.screen) : null
            return option && option.location !== 'mobile'
        }
        return true
    })

    const handleModalAction = (cta) => {
        const option = botMessageActionsNavigationOptions.find(b => b.value === cta.params?.screen)

        if (!option) return

        switch (option.value) {
            case 'joinRoom':
                openJoinRoom()
                break

            case 'createRoom':
            case 'createAgencyWideRoom':
            case 'createPrivateRoom':
            case 'createCrisisRoom':
                openCreateRoom()
                break

            case 'startThread':
                openCreateThread()
                break

            case 'invite':
            case 'inviteDepartment':
            case 'inviteRoom':
            case 'inviteContact':
                openInvite(cta.params?.type)
                break

            default:
                console.warn('Unhandled modal action:', option.value)
                break
        }
    }

    const onClick = async (cta) => {
        try {
            switch (cta.action) {
                case 'url': {
                    window.open(cta.params?.url, '_blank', 'noopener,noreferrer')
                    break
                }
                case 'drawer': {
                    toggleLeftNav('toggle')
                    break
                }
                case 'navigate': {
                    const option = messageActionController.getActionLocation(cta)
                    if (option && option.location === 'modal') {
                        handleModalAction(cta)
                    } else if (cta.params?.screen === 'thread' && cta.params?.threadId) {
                        navigate('/thread/' + cta.params.threadId)
                    } else if (cta.params?.screen === 'room' && cta.params?.roomId) {
                        navigate('/room/' + cta.params.roomId)
                    } else if (option) {
                        navigate(option?.location)
                    }
                    break
                }
                case 'threadMessageAction':
                    if (!message) return
                    // API call to threadMessages/id/action
                    setIsBusy(true)
                    await messageActionController.threadMessageAction(message, cta.params?.action)
                    break
                default:
                    break
            }
        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error.message || String(error)
            })
        } finally {
            setIsBusy(false)
        }
    }

    return (
        <Row className="message-action">
            <Col align={'left'} className="mt-3">
                <Row valign="center">
                    {validCtas.map((item, idx) =>
                        <Button
                            key={idx}
                            loading={isBusy}
                            color={(item.color === 'border') ? 'primary' : item.color || 'primary'}
                            onClick={() => onClick(item)}
                            outline={item.color === 'border'}
                            className="me-2"
                        >
                            <strong>{item.label}</strong>
                        </Button>
                    )}
                </Row>
            </Col>
            <Col xs={1} className="right-col"/>
        </Row>
    )
})

MessageBodyAction.displayName = 'MessageBodyAction'

export { MessageBodyAction }
